import React, { useEffect, useState } from 'react';
import { Bot, Save } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import AiUsageDetail, { textTokens } from '../components/AiUsageDetail';
import { useAuth } from '../context/AuthContext';
import { clienteTiene } from '../utils/features';
import BotConfigClasica from './BotConfigClasica';

// Largos máximos (todo lo que se escribe acá lo lee la IA en cada respuesta)
/* Los topes se subieron: el tono y las reglas quedaron cortos cuando hubo que
   escribir qué NO hacer —no usar "copado", no descartar a nadie—, que es
   justamente donde el detalle hace la diferencia. El límite viejo cortaba el
   texto al guardar, sin avisar. */
const LIMITS = { business_info: 6000, tone: 800, handoff_rules: 2000, seguimiento_texto: 500, lista: 8000 };

function CharCounter({ value, max }) {
  const len = (value || '').length;
  const pct = len / max;
  const color = pct >= 1 ? 'var(--danger)' : pct >= 0.85 ? '#d97706' : 'var(--text-muted)';
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12, marginTop: 4, color }}>
      <span>≈ {textTokens(len).toLocaleString('es-AR')} tokens por respuesta</span>
      <span style={{ fontWeight: 600 }}>{len.toLocaleString('es-AR')} / {max.toLocaleString('es-AR')}</span>
    </div>
  );
}

// Cada cosa en su solapa: antes era una sola página larga con todo mezclado
const SOLAPAS = [
  { key: 'general', label: 'General' },
  { key: 'negocio', label: 'Información del negocio' },
  { key: 'respuestas', label: 'Cómo responde' },
  { key: 'precios', label: 'Listas de precios' },
  { key: 'consumo', label: 'Consumo' },
];

/* Las solapas y lo que llevan (listas de precios, preguntar la ubicación) se
   hicieron para Punto Reciclaje. Los demás CRM siguen con la página de siempre. */
export default function BotConfig() {
  const { tenant } = useAuth();
  const conListas = clienteTiene(tenant, 'listas_precios');
  const conUbicacion = clienteTiene(tenant, 'ubicacion_lead');
  const conNombre = clienteTiene(tenant, 'nombre_lead');
  if (!conListas && !conUbicacion && !conNombre) return <BotConfigClasica />;
  return <BotConfigSolapas conListas={conListas} conUbicacion={conUbicacion} conNombre={conNombre} />;
}

function BotConfigSolapas({ conListas, conUbicacion, conNombre }) {
  const [cfg, setCfg] = useState(null);
  const [solapa, setSolapa] = useState('general');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/bot/settings')
      .then(res => setCfg({
        enabled: !!res.data.enabled,
        business_info: res.data.business_info || '',
        tone: res.data.tone || 'cordial y cercano',
        handoff_rules: res.data.handoff_rules || '',
        seguimiento_texto: res.data.seguimiento_texto || '',
        lista_minorista: res.data.lista_minorista || '',
        lista_mayorista: res.data.lista_mayorista || '',
        pedir_ubicacion: !!res.data.pedir_ubicacion,
        pedir_nombre: !!res.data.pedir_nombre,
      }))
      .catch(() => toast.error('No se pudo cargar la configuración'));
  }, []);

  const save = async () => {
    setSaving(true);
    try { await api.put('/bot/settings', cfg); toast.success('Configuración guardada'); }
    catch { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  if (!cfg) return <div style={{ color: 'var(--text-muted)', padding: 40 }}>Cargando...</div>;

  const set = (patch) => setCfg({ ...cfg, ...patch });
  const label = { fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6, display: 'block' };
  const help = { fontSize: 12, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.4 };

  const card = { marginBottom: 0 };
  const area = { fontFamily: 'inherit', fontSize: 13, resize: 'vertical' };
  const interruptor = (titulo, texto, valor, onChange) => (
    <div className="card" style={{ ...card, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
      <div><div style={{ fontWeight: 600 }}>{titulo}</div><div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{texto}</div></div>
      <label style={{ cursor: 'pointer' }}>
        <input type="checkbox" checked={valor} onChange={e => onChange(e.target.checked)} style={{ width: 'auto', transform: 'scale(1.4)' }} />
      </label>
    </div>
  );

  return (
    <div style={{ maxWidth: 1100 }}>
      <style>{`.bot-grid{display:grid;grid-template-columns:1fr 1fr;gap:16px;align-items:start}.bot-col{display:flex;flex-direction:column;gap:16px;min-width:0}@media(max-width:900px){.bot-grid{grid-template-columns:1fr}}`}</style>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
        <Bot size={24} color="var(--accent)" />
        <h1 style={{ fontSize: 26, fontWeight: 800, flex: 1 }}>Agente IA</h1>
        {/* Guardar queda arriba: vale para todas las solapas */}
        {solapa !== 'consumo' && <button className="btn btn-primary" onClick={save} disabled={saving}><Save size={16} /> {saving ? 'Guardando...' : 'Guardar cambios'}</button>}
      </div>
      <p style={{ color: 'var(--text-muted)', marginBottom: 16, fontSize: 14 }}>
        Configurá el asistente que responde automáticamente los mensajes (WhatsApp / Instagram) y deriva a una persona cuando hace falta.
      </p>

      <div className="filtro-fechas" style={{ marginBottom: 16 }}>
        <div className="filtro-fechas-fila">
          {SOLAPAS.filter(x => x.key !== 'precios' || conListas).map(x => (
            <button key={x.key} type="button" onClick={() => setSolapa(x.key)}
              className={'chip-periodo' + (solapa === x.key ? ' activo' : '')} aria-pressed={solapa === x.key}>{x.label}</button>
          ))}
        </div>
      </div>

      {/* General: lo que prende o apaga cosas */}
      {solapa === 'general' && (
        <div className="bot-col" style={{ maxWidth: 720 }}>
          {interruptor('Bot activado',
            cfg.enabled ? 'El bot responde automáticamente los mensajes entrantes.' : 'El bot está apagado: los mensajes entran pero nadie responde automático.',
            cfg.enabled, v => set({ enabled: v }))}
          {conUbicacion && interruptor('Preguntar de dónde es el cliente',
            cfg.pedir_ubicacion ? 'El bot le pregunta la localidad al empezar, si no la dijo, y la anota en la ubicación del lead.' : 'Apagado: el bot no pregunta la localidad ni la anota.',
            cfg.pedir_ubicacion, v => set({ pedir_ubicacion: v }))}
          {conNombre && interruptor('Preguntar el nombre del cliente',
            cfg.pedir_nombre ? 'Si el nombre del perfil de WhatsApp no sirve, el bot se lo pregunta al empezar y lo anota en el lead.' : 'Apagado: el bot no pregunta el nombre ni lo anota.',
            cfg.pedir_nombre, v => set({ pedir_nombre: v }))}
          <div className="card" style={card}>
            <label style={label}>Motor de IA</label>
            <div style={{ fontSize: 14, color: 'var(--text-primary)' }}>Claude Haiku 4.5</div>
            <div style={help}>Motor rápido y optimizado para atención por chat.</div>
          </div>
        </div>
      )}

      {/* Lo que sabe: la base de todas las respuestas */}
      {solapa === 'negocio' && (
        <div className="card" style={card}>
          <label style={label}>Información del negocio</label>
          <textarea value={cfg.business_info} maxLength={LIMITS.business_info} onChange={e => set({ business_info: e.target.value })} rows={20}
            placeholder={'Contale al bot todo lo que necesita saber para responder:\n\n- Qué vendés / qué servicios ofrecés\n- Horarios de atención\n- Ubicación y zonas de cobertura / envíos\n- Preguntas frecuentes y sus respuestas\n- Qué NO puede responder (ej: precios) → que derive a un asesor'}
            style={area} />
          <CharCounter value={cfg.business_info} max={LIMITS.business_info} />
          <div style={help}>El bot responde <strong>solo con esta información</strong>. Si le preguntan algo que no está acá, no lo inventa: avisa que un asesor va a contactar. Cuanto más completa, mejor responde.</div>
          <div style={{ ...help, marginTop: 6 }}>💡 Todo este texto lo lee la IA <b>en cada respuesta</b>: cuanto más largo, más consumo. Escribí lo esencial, en frases cortas y sin repetir información que ya esté en otro campo.</div>
        </div>
      )}

      {/* Cómo responde: el tono, cuándo pasar a una persona y el seguimiento */}
      {solapa === 'respuestas' && (
        <div className="bot-grid">
          <div className="bot-col">
            <div className="card" style={card}>
              <label style={label}>Tono / estilo</label>
              <textarea value={cfg.tone} maxLength={LIMITS.tone} onChange={e => set({ tone: e.target.value })} rows={4} placeholder="Ej: cordial y cercano, con algún emoji" style={area} />
              <CharCounter value={cfg.tone} max={LIMITS.tone} />
              <div style={help}>Cómo querés que hable el bot. Ej: "formal y profesional", "cercano y con emojis", "breve y directo".</div>
            </div>
            <div className="card" style={card}>
              <label style={label}>Mensaje de seguimiento</label>
              <textarea value={cfg.seguimiento_texto} maxLength={LIMITS.seguimiento_texto} onChange={e => set({ seguimiento_texto: e.target.value })} rows={3}
                placeholder={'Ej: ¿Seguís interesado? Si me contás un poco más de lo que necesitás, te ayudo a avanzar 🙂'} style={area} />
              <CharCounter value={cfg.seguimiento_texto} max={LIMITS.seguimiento_texto} />
              <div style={help}>
                Si un cliente deja de responder, el bot le escribe una vez más a las 20 horas. Sale a esa hora
                y no más tarde porque WhatsApp no entrega mensajes libres pasadas las 24 horas del último
                mensaje del cliente. Se manda una sola vez por conversación.
              </div>
            </div>
          </div>
          <div className="bot-col">
            <div className="card" style={card}>
              <label style={label}>¿Cuándo derivar a una persona?</label>
              <textarea value={cfg.handoff_rules} maxLength={LIMITS.handoff_rules} onChange={e => set({ handoff_rules: e.target.value })} rows={10}
                placeholder={'Ej: cuando pidan un presupuesto o precio concreto, quieran agendar un turno, o pidan hablar con un asesor.'} style={area} />
              <CharCounter value={cfg.handoff_rules} max={LIMITS.handoff_rules} />
              <div style={help}>Escribí en tus palabras cuándo el bot tiene que dejar de responder y pasarle la conversación a un vendedor. Igual, si el cliente pide expresamente hablar con una persona, siempre deriva.</div>
            </div>
          </div>
        </div>
      )}

      {/* Listas de precios: no las lee la IA, las pega el equipo en la charla */}
      {solapa === 'precios' && conListas && (<>
        <div style={{ ...help, marginTop: 0, marginBottom: 12, fontSize: 13 }}>
          En Conversaciones, cuando atiende una persona, aparecen los botones <b>Lista minorista</b> y <b>Lista mayorista</b>:
          pegan este texto en la respuesta para revisarlo antes de mandar. Siempre sale lo último que guardaste acá. El bot no usa estas listas.
        </div>
        <div className="bot-grid">
          {[['lista_minorista', 'Lista minorista'], ['lista_mayorista', 'Lista mayorista']].map(([k, t]) => (
            <div key={k} className="card" style={card}>
              <label style={label}>{t}</label>
              <textarea value={cfg[k]} maxLength={LIMITS.lista} onChange={e => set({ [k]: e.target.value })} rows={16}
                placeholder={'Ej:\nCobre: $X el kg\nBronce: $X el kg\nAluminio: $X el kg'} style={area} />
              <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'right', marginTop: 4 }}>{cfg[k].length.toLocaleString('es-AR')} / {LIMITS.lista.toLocaleString('es-AR')}</div>
            </div>
          ))}
        </div>
      </>)}

      {solapa === 'consumo' && <AiUsageDetail infoChars={cfg.business_info.length} />}
    </div>
  );
}
